import React from 'react';
import { Heart, Home, BookOpen, Users, Coffee, Utensils } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from './shared/SEO';
import PageLayout from './layout/PageLayout';
import CTASection from './shared/CTASection';
import PayPalDonateButton from './shared/PayPalDonateButton';

const DonatePage = () => {
  const donateSchema = {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    "name": "Donate to COTAD",
    "recipient": {
      "@type": "Organization",
      "name": "COTAD",
      "nonprofitStatus": "501(c)(3)"
    }
  };

  const impactAreas = [
    {
      icon: Coffee,
      title: "Coffee",
      description: "Provide a coffee for our hard working around the clock volunteers",
      amount: "$5 provides a coffee"
    },
    {
      icon: Utensils,
      title: "Warm Meal",
      description: "Provide a warm meal for a youth in need",
      amount: "$20 provides a warm meal"
    },
    {
      icon: BookOpen,
      title: "Educational Support",
      description: "Enable access to educational resources and programs",
      amount: "$50 supports educational materials and programs"
    },
    {
      icon: Home,
      title: "Emergency Housing",
      description: "Provide safe temporary housing for displaced youth",
      amount: "$100 helps provide a night of safe shelter"
    },
    {
      icon: Heart,
      title: "Counseling Services",
      description: "Support professional counseling sessions",
      amount: "$500 funds necessary counseling sessions"
    },
    {
      icon: Users,
      title: "Community Programs",
      description: "Fund community events and support groups",
      amount: "$1000 sponsors a month of community programs"
    },
  ];

  return (
    <>
      <SEO
        title="Donate to COTAD | Support Youth in Need"
        description="Support COTAD's mission to provide sanctuary and services to youth in need. Your tax-deductible donation makes a direct impact."
        pathname="/donate"
      >
        <script type="application/ld+json">
          {JSON.stringify(donateSchema)}
        </script>
      </SEO>

      <PageLayout
        title="Support Our Mission"
        description="Your donation helps us provide sanctuary and support to youth in need"
      >
        {/* Main Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="prose prose-lg text-center">
                <p>
                  As a 501(c)(3) nonprofit organization, we rely on the generosity of supporters like you to continue our mission. Every donation, no matter the size, makes a direct impact on the lives of youth in our community.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Impact Areas */}
        <section className="py-16 bg-bg-secondary">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-semibold text-center mb-12">Your Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {impactAreas.map((area, index) => (
                <div
                  key={index}
                  className="service-secondary impact-area-item"
                >
                  <area.icon className="w-8 h-8 text-accent mb-4" />
                  <h3 className="text-text-primary text-xl font-semibold">{area.title}</h3>
                  <p className="text-text-secondary mt-2">{area.description}</p>
                  <p className="text-accent font-semibold mt-4">{area.amount}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Donation Methods */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-semibold mb-6">Ways to Give</h2>
              <p className="text-lg text-text-secondary mb-8">
                All donations are tax-deductible. You will receive a receipt for your records.
              </p>
              
              <div className="bg-bg-secondary rounded-lg shadow-lg p-8">
                <p className="text-lg mb-4 text-text-primary">
                  Please support our mission by making a donation through PayPal.
                </p>
                <PayPalDonateButton />
              </div>
            </div>
          </div>
        </section>

        <CTASection
          variant="donate"
          title="Have questions about donating?"
          description="Contact us to learn more about how you can support our mission."
        />
      </PageLayout>
    </>
  );
};

export default DonatePage;